import React, { useEffect, useState } from 'react'
import BoardStore from '../../flux/boards/BoardStore'
import SettingsStore, {
    SettingsStoreState,
} from '../../flux/settings/SettingsStore'
import ControlLoadComponent from './ControlLoad'
import ControlSettingsLoadComponent from '../settings/ControlSettingsLoad'
import ControlCookiesConsentComponent from '../cookies/ControlCookiesConsent'
import { Consent } from '../../common/Enums'
import { isLocalStorageAvailable } from '../../repositories/LocalStorageUtilities'

/**
 * Properties accepted by the ControlLoadInitializeComponent
 */
export interface IControlLoadInitialiseComponentProps {
    children: JSX.Element | JSX.Element[]
}

/**
 * Reads the state of the board store.
 * If uninitialized then renders the ControlBoardsLoadComponent to
 * instigate loading of the store.
 *
 * If initialized then renders the boards.
 */
function ControlLoadInitialiseComponent(
    props: IControlLoadInitialiseComponentProps
) {
    const settingsStore = SettingsStore.GetInstance()
    const [settingsLoaded, setSettingsLoaded] = useState(false)
    const [boardsLoaded, setBoardsLoaded] = useState(false)
    const [cookieConsented, setCookieConsented] = useState(false)

    useEffect(() => {
        /**
         * Handler for state changed from board store
         */
        function stateChanged() {
            const _settingsLoaded =
                settingsStore.State === SettingsStoreState.Loaded
            setSettingsLoaded(_settingsLoaded)
            setBoardsLoaded(BoardStore.state === 'LOADED')
            setCookieConsented(
                !_settingsLoaded
                    ? false
                    : settingsStore.CookieConsent !== Consent.Undecided
            )
        }

        stateChanged()

        BoardStore.addStateChangeListener(stateChanged)
        settingsStore.addChangeListener(stateChanged)
        return () => {
            BoardStore.removeStateChangeListener(stateChanged)
            settingsStore.removeChangeListener(stateChanged)
        }
    }, [settingsStore])

    /**
     * 1. if settings store is unloaded, then "ControlSettingsLoadComponent"
     * 2. else if cookie consent undecided, then "ControlCookiesConsentComponent"
     * 3. else if board store is unloaded, then "ControlLoadComponent"
     * 4. else nothing, as everything is loaded
     * @returns {JSX.Element} React component to render
     */
    function getLoadComponents(): JSX.Element {
        if (!settingsLoaded) return <ControlSettingsLoadComponent />
        if (isLocalStorageAvailable() && !cookieConsented)
            return <ControlCookiesConsentComponent />
        if (!boardsLoaded) return <ControlLoadComponent />
        return <></>
    }

    return (
        <>
            {getLoadComponents()}
            {props.children}
        </>
    )
}

export default ControlLoadInitialiseComponent
